import { Button, Modal, UploadFile, notification } from "antd";
import React, { FC, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import Title from "antd/es/typography/Title";
import { useTranslation } from "react-i18next";
import { DynamicForm, FormInput, FormSelect } from "shared/ui";
import {
  getSupplierStatus,
  handleRequestError,
  setCompanyCustomerValue,
  setCompanySupplierValue,
} from "shared/helpers";
import {
  useCreateCompanyMutation,
  UnitItemWithLabel,
  useAddCompanyImageMutation,
  ProductCardItem,
  MaterialCategoriesItem,
} from "shared/api";
import { AddProductCardModal, ProductCardTable } from "pages/Companies";
import { useCompanyImageUpload, useModal } from "shared/hooks";
import { AdaptedValues, AddCompanyValues } from "shared/types";
import {
  isSupplierOrBuyerSelectValues,
  ModalSizeValue,
} from "shared/constants";
import { CategoriesSuppliedDynamicForm } from "./CategoriesSuppliedDynamicForm";
import { CompanyLogo } from "./CompanyLogo";

interface AddCompanyModalProps {
  isOpenModal: boolean;
  adaptedDimensionUnits: UnitItemWithLabel[];
  adaptedProductCard: AdaptedValues[];
  productCards?: ProductCardItem[];
  hideAddCompanyModal: () => void;
  adaptedMaterialCategories: AdaptedValues[];
  documentList: UploadFile[];
  handleDocumentList: (files: UploadFile[]) => void;
  parentCompanies: AdaptedValues[];
  materialCategories: MaterialCategoriesItem[];
  isLoadingProductCards: boolean;
  handleProductCardValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  productCardValue: string;
  clearProductCardValue: () => void;
  materialLabelOptions: { label: string; value: number | null }[];
  palletLabelOptions: { label: string; value: number | null }[];
}

const AddCompanyModal: FC<AddCompanyModalProps> = ({
  isOpenModal,
  adaptedDimensionUnits,
  adaptedProductCard,
  productCards,
  hideAddCompanyModal,
  adaptedMaterialCategories,
  documentList,
  handleDocumentList,
  parentCompanies,
  materialCategories,
  isLoadingProductCards,
  handleProductCardValue,
  productCardValue,
  clearProductCardValue,
  materialLabelOptions,
  palletLabelOptions,
}) => {
  const { t } = useTranslation();

  const {
    values,
    resetForm,
    handleBlur,
    handleChange,
    isValid,
    setFieldValue,
    errors,
    dirty,
    touched,
  } = useFormikContext<AddCompanyValues>();

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const { isCustomer, isSupplier } = getSupplierStatus(
    values?.is_supplier as string
  );

  const [api, contextHolder] = notification.useNotification();
  const [createCompany] = useCreateCompanyMutation();
  const [addCompanyImage] = useAddCompanyImageMutation();

  const [
    visibleProductCardModal,
    showProductCardsModal,
    hideProductCardsModal,
  ] = useModal();

  const [reorderingIds, setReorderingIds] = useState<number[]>([]);

  useEffect(() => {
    setCompanySupplierValue(values);
  }, [values?.is_supplier]);

  const handleModalClose = () => {
    hideAddCompanyModal();
    resetForm();
    handleDocumentList([]);
  };

  const {
    uploadedFile,
    previewLogoImage,
    uploadCompanyImageProps,
    closeModalAndClearUploadedImage,
  } = useCompanyImageUpload({
    documentList,
    handleDocumentList,
    closeModal: handleModalClose,
  });

  const closeModal = () => {
    const hasTouchedFields = Object.keys(touched).length > 0;
    if ((dirty && hasTouchedFields) || uploadedFile) {
      Modal.confirm({
        title: t("companies.warning-unsaved-changes-title", "Unsaved Changes"),
        content: t(
          "companies.warning-unsaved-changes-content",
          "You have unsaved changes. Are you sure you want to close this modal? All entered data will be lost."
        ),
        okButtonProps: {
          style: { background: themeColor },
        },
        onOk: handleModalClose,
      });
    } else {
      handleModalClose();
    }
  };

  const createCompanyRequest = async (ids: number[]) => {
    try {
      const response = await createCompany({
        name: values.name,
        address: values.address,
        payment_term_net_days: values.payment_term_net_days!,
        inco_terms: values.inco_terms,
        is_supplier: setCompanySupplierValue(values),
        is_buyer: setCompanyCustomerValue(values),
        config_dynamic_order_fields: values.sections!,
        product_cards: isSupplier ? [] : ids,
        evaluation_as_supplier: isCustomer
          ? null
          : values.evaluation_as_supplier!,
        categories_supplied: values.categories_supplied,
        parent_company: values.parent_company,
        child_companies: [],
        vat_number: values.vat_number?.length ? values.vat_number : null,
        email: values.email?.length ? values?.email : null,
        phone: values.phone?.length ? values.phone : null,
        contact_name: values?.contact_name?.length ? values.contact_name : null,
        information: values?.information?.length ? values.information : null,
        report_packaging: values.report_packaging,
        report_material_item: values.report_material_item,
        invoice_mapping_name: values.invoice_mapping_name,
      }).unwrap();

      if (response && uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);

        await addCompanyImage({
          id: response?.id,
          image: formData,
        }).unwrap();
      }

      api.success({
        message: t("companies.success-title"),
        description: t("companies.success-add-msg"),
      });
      hideAddCompanyModal();
    } catch (error) {
      const description = handleRequestError(
        error,
        t("users-page.error-title")
      );
      api.error({
        message: t("users-page.error-title"),
        description,
      });
    } finally {
      closeModalAndClearUploadedImage();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={t("companies.add-company-title")}
        open={isOpenModal}
        width={
          values?.sections?.length
            ? ModalSizeValue.XL_MEDIUM
            : ModalSizeValue.SECONDARY
        }
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${themeColor}`,
          },
        }}
        onCancel={closeModal}
        onOk={() => {
          createCompanyRequest(reorderingIds);
        }}
      >
        <CompanyLogo
          documentList={documentList}
          uploadCompanyImageProps={uploadCompanyImageProps}
          previewLogoImage={previewLogoImage}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.name")}
        </Title>

        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t("companies.name-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name}
          isError={!!errors.name}
        />

        <Title level={5}>{t("companies.parentCompany")}</Title>
        <FormSelect
          value={values?.parent_company}
          placeholder={t("companies.parent-company-placeholder")}
          fieldName="parent_company"
          options={parentCompanies}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          showSearch
        />

        <Title level={5}>
          {`${t("companies.is-supplier")} / ${t("companies.is-customer")}`}
        </Title>

        <FormSelect
          value={values.is_supplier || null}
          placeholder={t("companies.is-supplier-placeholder")}
          fieldName="is_supplier"
          options={isSupplierOrBuyerSelectValues}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.is_supplier?.toString()}
          isError={!!errors.is_supplier}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.contact-name")}
        </Title>

        <FormInput
          value={values.contact_name}
          fieldName="contact_name"
          placeholder={t("companies.contact-name-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.email")}
        </Title>

        <FormInput
          value={values.email}
          fieldName="email"
          placeholder={t("companies.email-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.email}
          isError={!!errors.email}
        />

        <Title level={5} className="mb-1.25">
          {t("material-management.invoice-mapping-name")}
        </Title>

        <FormInput
          value={values.invoice_mapping_name}
          fieldName="invoice_mapping_name"
          placeholder={t(
            "material-management.invoice-mapping-name-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.invoice_mapping_name}
          isError={!!errors.invoice_mapping_name}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.phone")}
        </Title>

        <FormInput
          value={values.phone}
          fieldName="phone"
          placeholder={t("companies.phone-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.phone}
          isError={!!errors.phone}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.vat-number")}
        </Title>

        <FormInput
          value={values.vat_number}
          fieldName="vat_number"
          placeholder={t("companies.vat-number-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.vat_number}
          isError={!!errors.vat_number}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.address")}
        </Title>

        <FormInput
          value={values.address}
          fieldName="address"
          placeholder={t("companies.address-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.address}
          isError={!!errors.address}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.payment-term")}
        </Title>

        <FormInput
          value={values.payment_term_net_days!}
          fieldName="payment_term_net_days"
          placeholder={t("companies.payment-term-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.payment_term_net_days}
          isError={!!errors.payment_term_net_days}
          numberType
        />

        <Title level={5} className="mb-1.25">
          {t("companies.inco-terms")}
        </Title>

        <FormInput
          value={values.inco_terms.toUpperCase()}
          fieldName="inco_terms"
          placeholder={t("companies.inco-terms-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.inco_terms}
          isError={!!errors.inco_terms}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.label-material-item")}
        </Title>
        <FormSelect
          value={values.report_material_item}
          fieldName="report_material_item"
          placeholder={t("companies.label-material-item-placeholder")}
          options={materialLabelOptions}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.label-pallet")}
        </Title>
        <FormSelect
          value={values.report_packaging}
          fieldName="report_packaging"
          placeholder={t("companies.label-pallet-placeholder")}
          options={palletLabelOptions}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.evaluation_as_supplier")}
        </Title>

        <FormSelect
          value={isCustomer ? null : values.evaluation_as_supplier}
          fieldName="evaluation_as_supplier"
          placeholder={t("companies.evaluation_as_supplier")}
          options={[
            { label: "1", value: 1 },
            { label: "2", value: 2 },
            { label: "3", value: 3 },
            { label: "4", value: 4 },
            { label: "5", value: 5 },
          ]}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.evaluation_as_supplier}
          isError={!!errors.evaluation_as_supplier}
          disabled={isCustomer}
        />

        <Title level={5} className="mb-1.25">
          {t("companies.information")}
        </Title>

        <FormInput
          textarea
          rows={5}
          value={values.information}
          fieldName="information"
          placeholder="companies.information-placeholder"
          handleChange={handleChange}
          handleBlur={handleBlur}
        />

        <div className="mt-2">
          <CategoriesSuppliedDynamicForm
            adaptedMaterialCategories={adaptedMaterialCategories}
            materialCategories={materialCategories}
          />
        </div>

        {!isSupplier && (
          <>
            <Button
              type="primary"
              style={{ backgroundColor: themeColor }}
              className="mt-2.5"
              onClick={showProductCardsModal}
            >
              {values.product_cards.length
                ? t("companies.add-edit-product-cards")
                : t("companies.add-product-card")}
            </Button>

            <AddProductCardModal
              isOpenModal={visibleProductCardModal}
              hideProductCardsModal={hideProductCardsModal}
              adaptedProductCard={adaptedProductCard}
              handleProductCardValue={handleProductCardValue}
              productCardValue={productCardValue}
              clearProductCardValue={clearProductCardValue}
            />
          </>
        )}

        {!isSupplier && values.product_cards.length ? (
          <ProductCardTable
            productCards={productCards}
            setReorderingIds={setReorderingIds}
            isLoadingProductCards={isLoadingProductCards}
          />
        ) : null}

        <DynamicForm adaptedDimensionUnits={adaptedDimensionUnits} />
      </Modal>
    </>
  );
};

export { AddCompanyModal };
